import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { safeZone } from "@simcheck/shared-types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * The browser's current IANA time zone (e.g. `America/New_York`), or `'UTC'` if
 * the environment can't report one. Used to stamp a sensible default at profile
 * creation and as the last fallback in {@link resolveTimeZone}.
 */
export function detectBrowserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Format a duration in milliseconds to a human-readable string
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

/** Options accepted by the date formatters — currently just an override zone. */
export interface DateFormatOptions {
  /**
   * IANA time zone to render in (e.g. `America/New_York`). When omitted the
   * value falls through to the browser's local zone — preserving the historical
   * behavior for call sites that don't yet thread a preference through.
   * Prefer the `useDateFormat()` hook, which binds the resolved user zone here.
   */
  timeZone?: string;
}

/**
 * Format a date to an absolute, locale-aware date+time string
 * (e.g., "May 27, 2026, 2:34 PM"). Returns '-' for missing/invalid input.
 * Pass `{ timeZone }` to render in a specific zone (else browser-local).
 */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  opts?: DateFormatOptions,
): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: opts?.timeZone ? safeZone(opts.timeZone) : undefined,
  });
}

/**
 * Sortable numeric date+time, 24-hour: "2026-10-12 11:30" (or
 * "2026-10-12 11:30:45" with `seconds`). Returns '-' for missing/invalid input.
 */
export function formatIsoDateTime(
  date: Date | string | number | null | undefined,
  opts?: DateFormatOptions & { seconds?: boolean },
): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(opts?.seconds ? { second: '2-digit' } : {}),
    hourCycle: 'h23',
    timeZone: opts?.timeZone ? safeZone(opts.timeZone) : undefined,
  }).formatToParts(d);
  const p = (type: Intl.DateTimeFormatPartTypes) => parts.find((x) => x.type === type)?.value ?? '';
  return `${p('year')}-${p('month')}-${p('day')} ${p('hour')}:${p('minute')}${opts?.seconds ? `:${p('second')}` : ''}`;
}

/**
 * Date only (e.g., "May 27, 2026"). Returns '-' for missing/invalid input.
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  opts?: DateFormatOptions,
): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: opts?.timeZone ? safeZone(opts.timeZone) : undefined,
  });
}

/**
 * Time only (e.g., "2:34 PM"). Returns '-' for missing/invalid input.
 */
export function formatTime(
  date: Date | string | number | null | undefined,
  opts?: DateFormatOptions,
): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: opts?.timeZone ? safeZone(opts.timeZone) : undefined,
  });
}

/**
 * Format a date to a relative time string (e.g., "5 minutes ago", "2 days ago")
 */
export function formatRelativeTime(date: Date | string | number | null | undefined): string {
  if (!date) return 'Never';

  const now = new Date();
  const then = new Date(date);
  const diff = now.getTime() - then.getTime();

  if (diff < 0) {
    return 'In the future';
  }

  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) {
    return seconds <= 1 ? 'Just now' : `${seconds} seconds ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }

  const years = Math.floor(months / 12);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}
